import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import {
  getErrorMessage,
  isAbortError,
  shouldRetryError,
} from './common/errors/api-error.ts';
import {
  addAppBreadcrumb,
  captureWithContext,
} from './common/telemetry/sentry.ts';
import { notifications } from '@mantine/notifications';
import {
  clearQueryErrorEventId,
  hasQueryErrorEventId,
  setQueryErrorEventId,
} from './main.tsx';

// *** ERROR DEBOUNCE *** //
const lastShownByKey = new Map<string, number>();
const ERROR_NOTIFICATION_COOLDOWN_MS = 15_000;

const shouldShowErrorNotification = (key: string) => {
  const now = Date.now();
  const lastShown = lastShownByKey.get(key) ?? 0;
  if (now - lastShown < ERROR_NOTIFICATION_COOLDOWN_MS) return false;
  lastShownByKey.set(key, now);
  return true;
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (isAbortError(error)) return;

      const eventId = captureWithContext(error, {
        operation: (query.meta?.operation ?? 'query') as string,
        queryKey: JSON.stringify(query.queryKey),
      });

      if (!hasQueryErrorEventId(query.queryKey)) {
        setQueryErrorEventId(query.queryKey, eventId);
      }

      if (shouldShowErrorNotification(JSON.stringify(query.queryKey))) {
        notifications.show({
          title: 'Loading failed',
          message: getErrorMessage(error),
          color: 'red',
        });
      }

      addAppBreadcrumb({
        category: 'query.error',
        message: 'React Query request failed',
        level: 'error',
        data: { queryKey: JSON.stringify(query.queryKey) },
      });
    },

    onSuccess: (_, query) => {
      clearQueryErrorEventId(query.queryKey);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (isAbortError(error)) return;

      const mutationKey = JSON.stringify(
        mutation.options.mutationKey ?? ['anonymous'],
      );

      if (shouldShowErrorNotification(`mutation:${mutationKey}`)) {
        notifications.show({
          title: 'Submission failed',
          message: getErrorMessage(error),
          color: 'red',
        });
      }

      captureWithContext(error, {
        operation: 'mutation',
        mutationKey,
      });

      addAppBreadcrumb({
        category: 'mutation.error',
        message: 'React Query mutation failed',
        level: 'error',
        data: { mutationKey },
      });
    },
  }),
  defaultOptions: {
    queries: {
      placeholderData: (prev: unknown) => prev,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => shouldRetryError(error, failureCount),
    },
    mutations: {
      retry: false,
    },
  },
});
